import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from 'i18n/i18n.types';
import { FormTemplate, FormTemplateSection, FormTemplateValidator } from 'data/template/template.types';
import * as types from 'data/network/network.types';
import Form, {
  FieldUpdateHandlerParam,
} from 'shared/form';
import './network-topology-filter-form.scss';
import { PORT_MAX_VALUE, validatePortNumberRange } from 'routes/Network/utils/validatePortNumberRange';
import FieldDesc from 'components/FieldDesc';
import { NetworkTopologyFilter } from 'data/network-topology/network-topology.types';
import { Button } from '@blueprintjs/core';
import NetworkTopologyCustomFilters from '../NetworkTopologyCustomFilters';

type Props = {
  template: FormTemplate,
  filters: NetworkTopologyFilter[],
  data: Partial<types.Network>,
  onFieldUpdate: (param: FieldUpdateHandlerParam) => void,
  onFilterSelection: (selected?: NetworkTopologyFilter) => void,
  onRemoveView: (id: string) => void,
  onClear: () => void,
  onSave: (name: string) => void,
  handleBlur: (e: any) => void,
}

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.Network
}

export const NEWORK_TOPOLOGY_FORM_I18N_KEY = 'networkTopology.form';

export const NetworkTopologyFilterForm = forwardRef<Ref, Props>(({
  template,
  filters,
  data,
  onFieldUpdate = () => null,
  onFilterSelection,
  onRemoveView,
  onClear,
  onSave,
  handleBlur
}: Props, ref) => {
  const mainRef = useRef<Form>(null);

  const isValid = (forceValidation = false, forceTouched = true) => {
    const isMainValid = mainRef.current?.isValid(forceValidation, forceTouched) ?? true;
    return isMainValid;
  };

  const getValues = () => {
    const {
      networkTopologyHint,
      ...values
    } = mainRef.current?.getValues();

    const formValues: types.Network = {
      ...values,
    };

    return formValues;
  };

  const { t } = useTranslation();

  const portNumberRangeValidator = (
    _propFields?: unknown,
    _stateFields?: unknown,
    _value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
  ) => {
    const { startPortNumber, portNumberRange } = getValues();

    const valid = validatePortNumberRange(+portNumberRange, +startPortNumber);

    let message = '';
    if (!valid && validator?.customValidationFunction) {
      const portValue = Math.min(PORT_MAX_VALUE, PORT_MAX_VALUE + 1 - +startPortNumber).toString();
      message = t(`${NEWORK_TOPOLOGY_FORM_I18N_KEY}.fieldError.${validator?.customValidationFunction}` as TranslationKey, { end: portValue }) ?? '';
    }

    return { isValid: valid, errorMessage: message };
  };

  const customValidationFunctionMap = {
    portNumberRangeValidator,
  };

  const customFieldComponentMap = {
    networkTopologyHint: FieldDesc,
  };

  useImperativeHandle(ref, () => ({
    isValid,
    getValues,
  }));

  return (
    <div className="network-topology-filter-form" data-testid="NetworkTopologyFilterForm">
      <div className="network-topology-filter-form-fields">
        <Form
          ref={mainRef}
          name={template.formId ?? template.key}
          defaults={data}
          fields={(template.main as FormTemplateSection).fields}
          layout={(template.main as FormTemplateSection).metadata.layout?.rows}
          fieldUpdateHandler={onFieldUpdate}
          customValidationFunctionMap={customValidationFunctionMap}
          customFieldComponentMap={customFieldComponentMap}
          i18nKeyPrefix={NEWORK_TOPOLOGY_FORM_I18N_KEY}
          handleBlur={handleBlur}
        />
      </div>
      <div>
        <Button title="ClearFilters" icon="reset" onClick={onClear} />
      </div>
      { /* Custom filters will be removed for now
      <NetworkTopologyCustomFilters
        filters={filters}
        onFilterSelection={onFilterSelection}
        onRemoveView={onRemoveView}
        onClear={onClear}
        onSave={onSave} /> */ }
    </div>
  );
});

export default NetworkTopologyFilterForm;
